<template>
  <div
    class="bg-gray-50 rounded-lg overflow-hidden shadow-sm hover:shadow-md border border-transparent hover:border-gray-600 transition-all duration-200 cursor-pointer group">
    <!-- Product Image -->
    <Link :href="route('products.show', { sku: product.sku })" class="block">
    <div class="aspect-square bg-gray-50 flex items-center justify-center p-8">
      <img
        :src="getThumbnailImage()"
        :alt="localizedName"
        class="max-w-full max-h-full object-contain"
        loading="lazy"
        decoding="async"
        @error="handleImageError"
        @load="handleImageLoad"
      />
    </div>
    </Link>

    <!-- Product Info -->
    <div class="p-4 bg-gray-50">
      <!-- Product Name -->
      <Link :href="route('products.show', { sku: product.sku })" class="block">
      <div class="text-lg font-semibold text-gray-900 mb-2 hover:text-sonnenglas-600 transition-colors" v-html="localizedName">
      </div>
      </Link>

      <!-- Pricing in reference format. POS materials have no UVP, only EK (feedback #07c). -->
      <div class="mb-3">
        <div class="text-sm text-gray-600">
          <template v-if="!product.isPos">{{ $t('UVP') }}: {{ formatPrice(product.price.value) }} - </template>{{ $t('EK ab') }} {{ formatPrice(getPurchasePrice()) }}
        </div>
      </div>

      <!-- Stock Status with color-coded dot and delivery time -->
      <div class="mb-4 flex items-center text-sm text-gray-700">
        <div :class="statusColor" class="w-2 h-2 rounded-full mr-2"></div>
        <template v-if="isDiscontinued">
          <span class="text-gray-500">{{ $t('Nicht mehr verfügbar') }}</span>
        </template>
        <template v-else-if="isSoldOut">
          <span class="text-red-600 font-medium">{{ $t('Ausverkauft') }}</span>
        </template>
        <template v-else-if="isInStock">
          <template v-if="hasAvailableQuantity">
            {{ $t(':available sofort verfügbar', { available: availableQuantity }) }}
          </template>
          <template v-else>
            {{ $t('Sofort verfügbar') }}
          </template>
        </template>
        <template v-else-if="isPreOrder">
          <span class="text-red-800 dark:text-red-700">
            {{ $t('Vorbestellung möglich') }} - {{ $t('Lieferung') }} {{ deliveryWeeks }} {{ isMonths ? (deliveryWeeks.includes('-') || parseInt(deliveryWeeks.replace('ca ', '')) > 1 ? $t('Monate') : $t('Monat')) : (deliveryWeeks.includes('-') || parseInt(deliveryWeeks) > 1 ? $t('Wochen') : $t('Woche')) }}
          </span>
        </template>
        <template v-else>
          {{ $t('Lieferung') }} {{ deliveryWeeks }} {{ isMonths ? (deliveryWeeks.includes('-') || parseInt(deliveryWeeks.replace('ca ', '')) > 1 ? $t('Monate') : $t('Monat')) : (deliveryWeeks.includes('-') || parseInt(deliveryWeeks) > 1 ? $t('Wochen') : $t('Woche')) }}
        </template>
      </div>

      <!-- Quantity & Add to Cart matching reference design -->
      <div class="flex items-center space-x-2">
        <select v-model="selectedQuantity"
          class="block w-16 text-sm border border-gray-300 rounded px-2 py-1 bg-white text-center focus:border-gray-400 focus:outline-none">
          <option v-for="qty in availableQuantities" :key="qty" :value="qty">
            {{ qty }}
          </option>
        </select>

        <button @click="addToCart" :disabled="isLoading || !isOrderable"
          class="flex items-center justify-center w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :title="$t('In den Warenkorb')">
          <svg v-if="isLoading" class="animate-spin w-4 h-4 text-gray-600" xmlns="http://www.w3.org/2000/svg"
            fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          <svg v-else class="w-4 h-4 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
            <path
              d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import type { Product } from '@/types/product'
import { useQuantityOptions } from '@/composables/useQuantityOptions'
import { useDeliveryStatus } from '@/composables/useDeliveryStatus'
import { getProductName } from '@/utils/localization'

const { props: pageProps } = usePage()
// Inject Ziggy route helper for script setup
const route = inject('route') as any

interface Props {
  product: Product
  tierPrice?: number
  currency?: string
}

interface Emits {
  (e: 'add-to-cart', product: Product, quantity: number): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const selectedQuantity = ref(1)
const isLoading = ref(false)

const localizedName = computed(() => {
  return getProductName(props.product)
})

// Use delivery status composable for consistent behavior across all components
const { isInStock, isDiscontinued, isSoldOut, deliveryWeeks, statusColor, isPreOrder, isMonths, availableQuantity, hasAvailableQuantity } = useDeliveryStatus(computed(() => props.product))

// Use the composable to get carton-based quantity options. POS materials are capped at 1; non-POS
// options are capped at the hard orderable limit (immediate available + usable replenishment).
const { quantityOptions } = useQuantityOptions(props.product.cartonQuantity ?? 1, props.product.isPos, props.product.maxOrderableQuantity)

const isOrderable = computed(() => {
  // Discontinued, hidden or sold-out products are not orderable
  return !props.product.hide && !isDiscontinued.value && !isSoldOut.value
})

const availableQuantities = computed(() => {
  if (!isOrderable.value) return [1]
  // Use carton-based quantity options from composable
  return quantityOptions.value
})

const getSizeInfo = (): string => {
  // Extract size information from product name or use dimensions
  const name = localizedName.value
  if (name.includes('1000ml')) return '1000ml'
  if (name.includes('250ml')) return '250ml'
  return ''
}

const formatPrice = (price: number): string => {
  // Use the currency prop first, then product currency, then default to EUR
  const currency = props.currency || props.product.currency || 'EUR'
  const locale = currency === 'CHF' ? 'de-CH' : 'de-DE'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency
  }).format(price)
}

const getPurchasePrice = (): number => {
  // POS materials are free.
  if (props.product.isPos) {
    return 0
  }
  // Use the real tier price if provided.
  if (props.tierPrice !== undefined && props.tierPrice > 0) {
    return props.tierPrice
  }
  // No tier price available: use an explicit fixed net price if present. Do NOT fabricate a
  // discount off the RRP (the old `price.value * 0.6` invented a price that was never real).
  return props.product.fixedNetPrice?.value ?? 0
}

// GLOBAL IMAGE CACHE: Prevent any duplicate requests across all components
const globalImageCache = (window as any)._imageCache || new Map()
if (!(window as any)._imageCache) {
  (window as any)._imageCache = globalImageCache
}

const imageLoaded = ref(false)
const errorOccurred = ref(false)

// Get the best thumbnail image URL with proper fallback chain
const getThumbnailImage = (): string => {
  const sku = props.product.sku.toLowerCase()
  const cacheKey = `${sku}_image`

  // Check global cache first
  if (globalImageCache.has(cacheKey)) {
    return globalImageCache.get(cacheKey)
  }

  let imageUrl: string

  // PRIORITY 1: Xhosa API thumbnail (optimized for listing pages)
  if (props.product.images?.thumbnail) {
    imageUrl = props.product.images.thumbnail
  }
  // PRIORITY 2: Xhosa API main image
  else if (props.product.images?.main) {
    imageUrl = props.product.images.main
  }
  // PRIORITY 3: Local thumbnail image
  else {
    const baseSku = sku.split('+')[0].split('-').slice(0, 2).join('-')
    imageUrl = `/images/products/${baseSku}_thumb.jpg`
  }

  // Cache the result
  globalImageCache.set(cacheKey, imageUrl)
  return imageUrl
}

// IMPROVED ERROR HANDLER: Fallback chain for images
const handleImageError = (event: Event) => {
  if (errorOccurred.value) return // Already handled

  const target = event.target as HTMLImageElement
  const sku = props.product.sku.toLowerCase()
  const baseSku = sku.split('+')[0].split('-').slice(0, 2).join('-')
  const placeholderUrl = '/images/products/placeholder.jpg'

  // Step 1: If API image failed, try local thumbnail
  if (target.src.includes('xhosa.sonnenglas.net')) {
    target.src = `/images/products/${baseSku}_thumb.jpg`
    return
  }

  // Step 2: If base SKU thumbnail failed, try full SKU thumbnail
  if (target.src.includes(`${baseSku}_thumb.jpg`) && baseSku !== sku) {
    target.src = `/images/products/${sku}_thumb.jpg`
    return
  }

  // Step 3: If thumbnail failed, try main image
  if (target.src.includes('_thumb.jpg')) {
    target.src = `/images/products/${baseSku}.jpg`
    return
  }

  // Step 4: Everything failed, use placeholder
  if (!target.src.includes('placeholder')) {
    target.src = placeholderUrl
    errorOccurred.value = true

    // Update global cache to prevent future attempts
    const cacheKey = `${sku}_image`
    globalImageCache.set(cacheKey, placeholderUrl)
  }
}

const handleImageLoad = () => {
  imageLoaded.value = true
}

const addToCart = async () => {
  if (!isOrderable.value || isLoading.value) return

  isLoading.value = true
  try {
    emit('add-to-cart', props.product, selectedQuantity.value)
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>